import { useState } from "react";
import { Button, Select, Spinner } from "flowbite-react";
import { HiHashtag } from "react-icons/hi";
import { Link } from "react-router-dom";

import { useAuth, useToast } from "../../../hooks";
import { integrationPath } from "../../../routes";
import { NOTIFICATION_TYPES } from "../../../utils";

const CHANNELS_ENDPOINT = "integration/team-integrations/slack-channels/";

/**
 * Show + edit the Slack channel linked to an account (Phase 2, Task 2.3).
 * Reads the current name from the account; writes via the workspace-validated
 * accounts/{id}/slack-channel action.
 */
export const SlackChannelCard = ({ accountId, channelName, onChanged }) => {
  const { apiClient } = useAuth();
  const showToast = useToast();

  const [editing, setEditing] = useState(false);
  const [channels, setChannels] = useState([]);
  const [selected, setSelected] = useState("");
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notConnected, setNotConnected] = useState(false);
  const [inviteHint, setInviteHint] = useState(null);

  const startEditing = async () => {
    setEditing(true);
    setLoadingChannels(true);
    setNotConnected(false);
    try {
      const { data } = await apiClient.get(CHANNELS_ENDPOINT);
      setChannels(data.channels || []);
    } catch (err) {
      if (err.response?.data?.error === "not_connected") {
        setNotConnected(true);
      } else {
        showToast({
          content: "Couldn't load Slack channels. Please try again.",
          type: NOTIFICATION_TYPES.error,
        });
        setEditing(false);
      }
    } finally {
      setLoadingChannels(false);
    }
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await apiClient.put(`accounts/${accountId}/slack-channel/`, {
        channel_id: selected,
      });
      // Task 2.4: the bot self-joins public channels; private/Slack Connect need a manual invite.
      setInviteHint(
        res?.data?.needs_manual_invite ? res.data.channel_type : null,
      );
      setEditing(false);
      setSelected("");
      if (onChanged) await onChanged();
    } catch (err) {
      showToast({
        content: "Couldn't link that channel. Please try again.",
        type: NOTIFICATION_TYPES.error,
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await apiClient.delete(`accounts/${accountId}/slack-channel/`);
      setInviteHint(null);
      if (onChanged) await onChanged();
    } catch (err) {
      showToast({
        content: "Couldn't remove the channel. Please try again.",
        type: NOTIFICATION_TYPES.error,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
        <h2 className="text-base font-semibold text-gray-900">Slack channel</h2>
        {!editing && (
          <Button size="xs" color="light" onClick={startEditing}>
            {channelName ? "Edit" : "Link channel"}
          </Button>
        )}
      </div>

      <div className="px-5 py-4">
        {!editing && (
          <div className="flex items-center justify-between">
            {channelName ? (
              <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-900">
                <HiHashtag className="size-4 text-gray-400" />
                {channelName}
              </span>
            ) : (
              <span className="text-sm text-gray-500">Not linked</span>
            )}
            {channelName && (
              <button
                type="button"
                className="text-sm text-red-600 hover:underline disabled:opacity-50"
                onClick={remove}
                disabled={saving}
              >
                Remove
              </button>
            )}
          </div>
        )}

        {!editing && inviteHint && (
          <p className="mt-3 rounded-md bg-amber-50 p-2.5 text-sm text-amber-800">
            Panora can&apos;t auto-join a{" "}
            {inviteHint === "slack_connect" ? "Slack Connect" : "private"}{" "}
            channel. In Slack, run <code>/invite @Panora</code> in this channel
            so Panora can read it.
          </p>
        )}

        {editing && notConnected && (
          <p className="text-sm text-gray-500">
            <Link
              to={integrationPath("slack")}
              className="font-medium text-blue-600 hover:underline"
            >
              Connect Slack
            </Link>{" "}
            first, then invite the bot to the client&apos;s channel.
          </p>
        )}

        {editing && !notConnected && (
          <div className="space-y-3">
            {loadingChannels ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Spinner size="sm" /> Loading channels…
              </div>
            ) : (
              <Select
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
              >
                <option value="">Select a channel…</option>
                {channels.map((c) => (
                  <option key={c.id} value={c.id}>
                    #{c.name}
                    {c.is_member ? "" : " (bot not invited)"}
                  </option>
                ))}
              </Select>
            )}
            <div className="flex justify-end gap-2">
              <Button
                size="xs"
                color="light"
                onClick={() => {
                  setEditing(false);
                  setSelected("");
                }}
              >
                Cancel
              </Button>
              <Button
                size="xs"
                color="blue"
                onClick={save}
                disabled={!selected || saving}
                isProcessing={saving}
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
